<template>
  <div v-show="isActiveStep" class="mt-5 flex flex-col">
    <div class="flex flex-col gap-3">
      <VcRadioButton
        id="noBox"
        v-model="valueSelected"
        value="false"
        name="exportSelection"
        class="rounded-md font-semibold"
        :label="$t('common.buttons.no')"
      >
      </VcRadioButton>
      <VcRadioButton
        id="yesBox"
        v-model="valueSelected"
        value="true"
        name="exportSelection"
        class="rounded-md font-semibold"
        :label="$t('pages.checkout.export_items_yes')"
      >
      </VcRadioButton>
    </div>

    <div v-if="isResellerQuestionEnabled && showExportInfo" class="flex items-start gap-2 pt-5 font-medium">
      <VcIcon name="information-circle" class="size-5 shrink-0"></VcIcon>
      <span v-html-safe="$t('pages.checkout.export_items_info')"></span>
    </div>

    <div class="mb-4 mt-5 flex w-full flex-col gap-4 md:w-fit">
      <VcButton
        color="primary"
        variant="solid"
        type="submit"
        class="mt-3 min-h-[48px]"
        size="lg"
        :disabled="!valueSelected"
        @click="nextStepHandle"
      >
        {{
          isResellerQuestionEnabled
            ? $t(props.buttonText ?? "pages.checkout.save_and_continue_payment")
            : $t("pages.checkout.save_and_continue_payment")
        }}
      </VcButton>
    </div>
  </div>

  <div v-show="props.isCompletedStep && !props.isActiveStep" class="mb-2">
    <div class="flex flex-col gap-3.5 font-medium leading-6">
      <span class="font-medium">
        {{ $t("pages.checkout.answered") }}
        {{ intendsToExport ? $t("pages.checkout.export_items_yes") : $t("common.buttons.no") }}
      </span>
      <span v-if="!isResellerQuestionEnabled && intendsToExport" class="italic">{{
        isEusDocusignFlow ? $t("pages.checkout.answered_yes_docusign_email_flow") : $t("pages.checkout.answered_yes")
      }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useCurrency } from "@/core/composables/useCurrency";
import { EUS_DOCUSIGN_CURRENCIES } from "@/core/constants/orders";
import { hasNonEmptyLocaleMessage } from "@/core/utilities/i18n";
import { useCheckoutFacade } from "@/shared/checkout/composables/useCheckoutFacade";
import { useResellerQuestion } from "@/shared/thorlabs/shared/composables/useResellerQuestion";

const props = defineProps<IProps>();
const i18n = useI18n();
const { intendsToExport, hasCompletedExportStep } = useCheckoutFacade();
const { isResellerQuestionEnabled } = useResellerQuestion();
const { currentCurrency } = useCurrency();
const valueSelected = ref<string>(intendsToExport.value ? "true" : "");

const isEusDocusignFlow = computed(() => EUS_DOCUSIGN_CURRENCIES.has(currentCurrency.value.code));
const showExportInfo = computed(() => hasNonEmptyLocaleMessage("pages.checkout.export_items_info", i18n));

interface IProps {
  isCompletedStep?: boolean;
  isActiveStep?: boolean;
  nextStep?: string;
  buttonText?: string;
}
const router = useRouter();
const route = useRoute();

async function nextStepHandle() {
  if (props.nextStep) {
    hasCompletedExportStep.value = true;
    await router.push({ name: props.nextStep, query: route.query, replace: true });

    // Wait for DOM update, then scroll next step into view with header offset
    requestAnimationFrame(() => {
      const activeStepEl = document.querySelector('[data-step="active"]') as HTMLElement;
      if (activeStepEl) {
        const headerOffset = 100; // sticky header height
        const elementPosition = activeStepEl.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    });
  }
}

watch(valueSelected, (val) => {
  intendsToExport.value = val === "true";
});

watch(
  () => props.isActiveStep,
  (active) => {
    if (active) {
      hasCompletedExportStep.value = false;
    }
  },
);
</script>

<style lang="scss" scoped>
:deep(a) {
  color: var(--link-color);
}
</style>
